import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share, Plus, X, Sparkles } from 'lucide-react';
import {
  shouldShowIosInstallPrompt,
  supportsMacAddToDock,
  dismissInstallPrompt,
  isMacSafari,
  isStandalone,
} from '../../lib/pwa/installDetect';
import { trackPwaEvent } from '../../lib/pwa/track';

/**
 * "Install DutchDate" floating prompt for Apple browsers.
 *
 *  - iOS Safari (iPhone / iPad in Safari) → bottom sheet with step-by-step
 *    Share → "Add to Home Screen" instructions, since Apple doesn't expose
 *    `beforeinstallprompt` so we can't trigger natively.
 *  - macOS Sonoma Safari (Safari 17+) → small top-right banner pointing the
 *    user at the File → "Add to Dock" menu item.
 *  - Already-installed (display-mode: standalone), Chrome on iOS, or
 *    previously-dismissed users → renders nothing.
 *
 * Shows after a 5-second delay so it doesn't punch users in the face on
 * first visit; the goal is to nudge engaged sessions, not bounce new ones.
 */
export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  // 'ios' shows the bottom sheet, 'mac' shows the top banner.
  const [variant, setVariant] = useState<'ios' | 'mac' | null>(null);

  useEffect(() => {
    if (isStandalone()) return; // Already installed, nothing to do.
    const t = setTimeout(() => {
      // Defer install prompt until the cookie banner has been dismissed — both
      // anchor to the bottom of the screen on mobile and stacking two
      // dismiss-required cards on first paint is bad UX.
      try {
        if (!localStorage.getItem('dd_cookie_consent_v1')) return;
      } catch { /* ignore */ }
      if (shouldShowIosInstallPrompt()) {
        setVariant('ios');
        setVisible(true);
        trackPwaEvent('prompt_shown');
      } else if (supportsMacAddToDock() && isMacSafari()) {
        // Re-use the same dismissal key — once they dismiss it on any device
        // they don't want to see it again.
        try { if (localStorage.getItem('dd_a2hs_dismissed') === '1') return; } catch { /* ignore */ }
        setVariant('mac');
        setVisible(true);
        trackPwaEvent('prompt_shown');
      }
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = () => {
    trackPwaEvent('prompt_dismissed');
    dismissInstallPrompt();
    setVisible(false);
  };

  if (!variant) return null;

  return (
    <AnimatePresence>
      {visible && variant === 'ios' && (
        <motion.div
          data-testid="ios-install-prompt"
          className="fixed inset-x-0 bottom-0 z-[70] px-3 pb-[max(env(safe-area-inset-bottom),12px)] sm:bottom-6 sm:right-6 sm:left-auto sm:max-w-sm sm:px-0 sm:pb-0"
          initial={{ y: 200, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 200, opacity: 0 }}
          transition={{ type: 'spring', damping: 26, stiffness: 280 }}
        >
          <div className="bg-white rounded-3xl shadow-2xl border border-neutral-100 p-4 relative">
            <button
              data-testid="ios-install-dismiss"
              type="button"
              onClick={handleDismiss}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-neutral-50 hover:bg-neutral-100 flex items-center justify-center text-neutral-400"
              aria-label="Dismiss install prompt"
            >
              <X size={14} />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <div className="w-10 h-10 rounded-xl bg-dutch-teal/10 flex items-center justify-center shrink-0">
                <Sparkles size={18} className="text-dutch-teal" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-heading font-bold text-neutral-900 leading-tight">Install DutchDate</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">Open it like a real app — full-screen, push notifications, faster.</p>
              </div>
            </div>
            <ol className="mt-3 space-y-1.5 text-[12px] text-neutral-700">
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-dutch-teal text-white text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
                Tap the <Share size={12} className="inline text-dutch-teal mx-0.5" /> Share button in Safari
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-dutch-teal text-white text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
                Scroll &amp; tap <span className="font-semibold mx-0.5">Add to Home Screen</span> <Plus size={12} className="inline text-dutch-teal" />
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-dutch-teal text-white text-[10px] font-bold flex items-center justify-center shrink-0">3</span>
                Tap <span className="font-semibold mx-0.5">Add</span> — done!
              </li>
            </ol>
          </div>
        </motion.div>
      )}

      {visible && variant === 'mac' && (
        <motion.div
          data-testid="mac-install-prompt"
          className="fixed top-4 right-4 z-[70] max-w-sm"
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -80, opacity: 0 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-neutral-100 p-3 pr-9 relative">
            <button
              data-testid="mac-install-dismiss"
              type="button"
              onClick={handleDismiss}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-neutral-50 hover:bg-neutral-100 flex items-center justify-center text-neutral-400"
              aria-label="Dismiss install prompt"
            >
              <X size={14} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-dutch-teal/10 flex items-center justify-center shrink-0">
                <Sparkles size={16} className="text-dutch-teal" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-neutral-900 leading-tight">Add DutchDate to your Dock</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">In Safari: <span className="font-semibold">File → Add to Dock</span></p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
